import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { IApiUserInfo, IUser } from '@core/models';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { setUser } from '@store/actions/user.actions';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly http: HttpClient
  ) {}

  start() {
    /*
     * Listening events from other tabs
     * This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when
     * a user logs in or out of another tab or window
     */
    this.authService.instance.enableAccountStorageEvents();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === 'msal:accountRemoved') this.router.navigate(['/']);

        if (result.eventType === 'msal:accountAdded') this.router.navigate(['dashboard']);
      });

    /**
     * Listens when a user is logged in
     */
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      /**
       * Set Roles
       * Important the delay(setTimeOut) is to wait for the storage to write the accessToken
       */

      setTimeout(() => {
        this.authService.instance.handleRedirectPromise().then((response) => {
          const currentAccounts = this.authService.instance.getAllAccounts();
          const accountToUse = response?.account ?? currentAccounts[0];

          if (!accountToUse) {
            return;
          }

          // Ensure MSAL has an active account before protected API calls.
          this.authService.instance.setActiveAccount(accountToUse);

          this.getRoles().subscribe({
            next: (userInfoApi) => {
              this.setActiveAccount(accountToUse, userInfoApi);
              this.router.navigate(['dashboard']);
            },
          });
        });
      }, 200);
    }
  }

  setActiveAccount(firstAccount: any, userInfoApi: any) {
    this.authService.instance.setActiveAccount(firstAccount);
    const user: IUser = {
      email: firstAccount.username,
      name: firstAccount.name ?? '',
      role: userInfoApi.roles ?? [],
    };
    this.store.dispatch(setUser({ value: user }));
  }

  getRoles() {
    return this.http.get<IApiUserInfo>(`${environment.api_root}/v1/internal-user/info`);
  }
}
